/**
 * useConfirmDialog — Global confirm dialog composable
 * Replaces native confirm() with a styled, accessible dialog.
 *
 * Usage:
 *   const { confirm } = useConfirmDialog()
 *   const ok = await confirm({ title: 'Elimina', message: 'Sicuro?' })
 *   if (ok) { ... }
 */

import { ref, readonly } from 'vue'

export type ConfirmVariant = 'danger' | 'warning' | 'info'

export interface ConfirmOptions {
  title?: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: ConfirmVariant
}

interface ConfirmState {
  visible: boolean
  title: string
  message: string
  confirmText: string
  cancelText: string
  variant: ConfirmVariant
  resolve: ((value: boolean) => void) | null
}

const state = ref<ConfirmState>({
  visible: false,
  title: 'Conferma',
  message: '',
  confirmText: 'Conferma',
  cancelText: 'Annulla',
  variant: 'danger',
  resolve: null,
})

export function useConfirmDialog() {
  function confirm(opts: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      state.value = {
        visible: true,
        title: opts.title ?? 'Conferma',
        message: opts.message,
        confirmText: opts.confirmText ?? 'Conferma',
        cancelText: opts.cancelText ?? 'Annulla',
        variant: opts.variant ?? 'danger',
        resolve,
      }
    })
  }

  function handleConfirm() {
    state.value.resolve?.(true)
    state.value.visible = false
    state.value.resolve = null
  }

  function handleCancel() {
    state.value.resolve?.(false)
    state.value.visible = false
    state.value.resolve = null
  }

  return {
    state: readonly(state),
    confirm,
    handleConfirm,
    handleCancel,
  }
}
